/**
 * Scorecard Review Component
 * Review and approve submitted scorecards for golf outings.
 */
import React, { useState, useEffect } from 'react';
import { ClipboardList, Check, AlertTriangle, Eye, Flag, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';
import { useOutingApi } from '../hooks/useOutingApi';
import { useOuting } from '../context/OutingContext';
import { getScoreStyleClass, calculateRelativeScore } from '../utils/outingHelpers';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'gray', icon: '⏳' },
  submitted: { label: 'Submitted', color: 'yellow', icon: '📝' },
  approved: { label: 'Approved', color: 'green', icon: '✓' },
  flagged: { label: 'Flagged', color: 'red', icon: '⚠️' }
};

export default function ScorecardReview({ outingId, user, onRefresh }) {
  const api = useOutingApi();
  const { scorecards, teams, fetchScorecards } = useOuting();
  const [expandedCard, setExpandedCard] = useState(null);
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [flaggingScorecard, setFlaggingScorecard] = useState(null);
  const [flagReason, setFlagReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchScorecards(outingId);
  }, [outingId, fetchScorecards]);

  const handleApprove = async (scorecardId) => {
    setProcessing(true);
    try {
      await api.approveScorecard(outingId, scorecardId, user.user_id, user.display_name || 'Organizer');
      toast.success('Scorecard approved!');
      fetchScorecards(outingId);
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleFlag = async () => {
    if (!flagReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    
    setProcessing(true);
    try {
      await api.flagScorecard(outingId, flaggingScorecard.id, flagReason, user.user_id);
      toast.success('Scorecard flagged for review');
      setFlagModalOpen(false);
      setFlaggingScorecard(null);
      setFlagReason('');
      fetchScorecards(outingId);
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleApproveAll = async () => {
    const toApprove = scorecards.filter(s => s.status === 'submitted');
    if (toApprove.length === 0) {
      toast.info('No scorecards to approve');
      return;
    }
    
    setProcessing(true);
    try {
      for (const sc of toApprove) {
        await api.approveScorecard(outingId, sc.id, user.user_id, user.display_name || 'Organizer');
      }
      toast.success(`Approved ${toApprove.length} scorecards!`);
      fetchScorecards(outingId);
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const pending = scorecards.filter(s => s.status === 'pending' || s.status === 'submitted');
  const approved = scorecards.filter(s => s.status === 'approved');
  const flagged = scorecards.filter(s => s.status === 'flagged');

  // Teams without scorecards
  const teamsWithScorecards = new Set(scorecards.map(s => s.team_id));
  const teamsWaiting = teams.filter(t => !teamsWithScorecards.has(t.id));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white">Scorecards</h3>
          <p className="text-sm text-zinc-400">
            {approved.length} approved, {pending.length} pending, {flagged.length} flagged
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchScorecards(outingId)}
            className="border-white/20"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          {pending.length > 0 && (
            <Button
              size="sm"
              onClick={handleApproveAll}
              disabled={processing}
              className="bg-green-600 hover:bg-green-500"
              data-testid="approve-all-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Approve All ({pending.length})
            </Button>
          )}
        </div>
      </div>

      {/* Waiting for Submissions */}
      {teamsWaiting.length > 0 && (
        <Card className="bg-yellow-500/10 border-yellow-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <ClipboardList className="w-5 h-5 text-yellow-400" />
              <span className="font-semibold text-yellow-300">Waiting for Submissions</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {teamsWaiting.map(team => (
                <Badge key={team.id} variant="outline" className="text-yellow-400 border-yellow-500/50">
                  {team.name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scorecards List */}
      {scorecards.length === 0 ? (
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-8 text-center">
            <ClipboardList className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h4 className="font-semibold text-white mb-2">No Scorecards Yet</h4>
            <p className="text-zinc-400 text-sm">Scorecards will appear here once teams submit them</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {scorecards.map(scorecard => {
            const statusConfig = STATUS_CONFIG[scorecard.status] || STATUS_CONFIG.pending;
            const isExpanded = expandedCard === scorecard.id;
            
            return (
              <Collapsible
                key={scorecard.id}
                open={isExpanded}
                onOpenChange={() => setExpandedCard(isExpanded ? null : scorecard.id)}
              >
                <Card className={`bg-[#121212] border-white/10 ${
                  scorecard.status === 'flagged' ? 'border-red-500/50' : ''
                }`}>
                  <CardContent className="p-4">
                    <CollapsibleTrigger className="w-full">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            scorecard.status === 'approved' ? 'bg-green-500/20' :
                            scorecard.status === 'flagged' ? 'bg-red-500/20' : 'bg-yellow-500/20'
                          }`}>
                            <span className="text-lg">{statusConfig.icon}</span>
                          </div>
                          <div className="text-left">
                            <p className="font-semibold text-white">{scorecard.team_name}</p>
                            <p className="text-sm text-zinc-400">
                              Score: {scorecard.team_score || '—'} 
                              {scorecard.relative_score !== null && (
                                <span className={scorecard.relative_score <= 0 ? 'text-green-400' : 'text-red-400'}>
                                  {' '}({scorecard.relative_score > 0 ? '+' : ''}{scorecard.relative_score})
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Badge className={`bg-${statusConfig.color}-500/20 text-${statusConfig.color}-400`}>
                            {statusConfig.label}
                          </Badge>
                          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    
                    <CollapsibleContent>
                      <div className="mt-4 pt-4 border-t border-white/10 space-y-4">
                        {/* Hole by Hole Scores */}
                        <div>
                          <p className="text-sm text-zinc-400 mb-2">Hole-by-Hole Scores</p>
                          <div className="grid grid-cols-9 gap-1 text-center text-xs">
                            {/* Front 9 */}
                            {scorecard.holes?.slice(0, 9).map((hole, i) => (
                              <div key={i} className="space-y-1">
                                <div className="text-zinc-500">{i + 1}</div>
                                <div className="text-zinc-400">{hole.par}</div>
                                <div className={`font-semibold ${getScoreStyleClass(hole.score, hole.par)}`}>
                                  {hole.score || '—'}
                                </div>
                                {hole.is_ctp_hole && (
                                  <Badge className="text-[8px] bg-yellow-500/20 text-yellow-400">CTP</Badge>
                                )}
                              </div>
                            ))}
                          </div>
                          
                          {scorecard.holes?.length > 9 && (
                            <div className="grid grid-cols-9 gap-1 text-center text-xs mt-2 pt-2 border-t border-white/5">
                              {/* Back 9 */}
                              {scorecard.holes?.slice(9, 18).map((hole, i) => (
                                <div key={i} className="space-y-1">
                                  <div className="text-zinc-500">{i + 10}</div>
                                  <div className="text-zinc-400">{hole.par}</div>
                                  <div className={`font-semibold ${getScoreStyleClass(hole.score, hole.par)}`}>
                                    {hole.score || '—'}
                                  </div>
                                  {hole.is_ctp_hole && (
                                    <Badge className="text-[8px] bg-yellow-500/20 text-yellow-400">CTP</Badge>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Flags */}
                        {scorecard.flags?.length > 0 && (
                          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                            <p className="text-sm font-semibold text-red-400 mb-1">Issues Flagged:</p>
                            <ul className="text-sm text-red-300 list-disc list-inside">
                              {scorecard.flags.map((flag, i) => (
                                <li key={i}>{flag}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Actions */}
                        {scorecard.status !== 'approved' && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleApprove(scorecard.id)}
                              disabled={processing}
                              className="bg-green-600 hover:bg-green-500"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setFlaggingScorecard(scorecard);
                                setFlagModalOpen(true);
                              }}
                              className="border-red-500 text-red-400 hover:bg-red-500/20"
                            >
                              <Flag className="w-4 h-4 mr-1" />
                              Flag
                            </Button>
                          </div>
                        )}
                        
                        {scorecard.approved_at && (
                          <p className="text-xs text-zinc-500">
                            Approved by {scorecard.approved_by} on {new Date(scorecard.approved_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </CollapsibleContent>
                  </CardContent>
                </Card>
              </Collapsible>
            );
          })}
        </div>
      )}

      {/* Flag Modal */}
      <Dialog open={flagModalOpen} onOpenChange={setFlagModalOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Flag Scorecard</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-zinc-400">
              Flagging: <span className="text-white font-semibold">{flaggingScorecard?.team_name}</span>
            </p>
            <div>
              <label className="text-sm text-zinc-400">Reason for flagging</label>
              <Textarea
                value={flagReason}
                onChange={(e) => setFlagReason(e.target.value)}
                placeholder="e.g., Hole 12 score seems too low, please verify"
                className="bg-[#1A1A1A] border-white/20 mt-1"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setFlagModalOpen(false)}
                className="flex-1 border-white/20"
              >
                Cancel
              </Button>
              <Button
                onClick={handleFlag}
                disabled={processing}
                className="flex-1 bg-red-600 hover:bg-red-500"
              >
                <Flag className="w-4 h-4 mr-2" />
                Flag Scorecard
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
